import styled, { css } from 'styled-components';
import { TextField } from '@material-ui/core';
import { lightTheme as theme } from '../../../shared/theme';

const bannerPalette = {
    success: {
        background: theme.colors.successBg,
        border: theme.colors.success,
        color: theme.colors.success,
    },
    error: {
        background: theme.colors.dangerBg,
        border: theme.colors.danger,
        color: theme.colors.danger,
    },
};

export const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const MainContent = styled.div`
    background: ${theme.colors.background};
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const ContentAlignedContainer = styled.div`
    width: 100%;
`;

export const SearchHeaderSection = styled.div`
    position: relative;
    isolation: isolate;
    margin: -24px 0 8px;
    padding: 14px 0 16px;

    &::before {
        content: '';
        position: absolute;
        z-index: -1;
        top: 0;
        bottom: 0;
        left: 50%;
        width: 100vw;
        transform: translateX(-50%);
        background: ${theme.colors.primary};
        border-top: 1px solid rgba(255, 255, 255, 0.2);
        border-bottom: 1px solid ${theme.colors.textMuted}33;
    }

    @media (max-width: 768px) {
        margin: -16px 0 8px;
        padding: 12px 0 14px;
    }
`;

export const FeedbackBanner = styled.div`
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 14px;
    line-height: 1.4;
    box-shadow: ${theme.colors.shadowSm};

    ${({ $variant }) => {
        const colors = bannerPalette[$variant] || bannerPalette.error;

        return css`
            color: ${colors.color};
            background: ${colors.background};
            border: 1px solid ${colors.border};
        `;
    }}
`;

export const SearchHero = styled.section`
    background: transparent;
    border-radius: 0;
    padding: 0;
    box-shadow: none;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    width: 100%;
`;

export const SearchHeroTitle = styled.h2`
    margin: 0;
    width: 100%;
    text-align: center;
    color: ${theme.colors.paper};
    font-size: 1.45rem;
    font-weight: 700;
`;

export const SearchInput = styled(TextField)`
    width: 100%;

    .MuiOutlinedInput-root {
        height: 46px;
        width: 100%;
        background: ${theme.colors.paper};
        border-radius: 12px;
        box-shadow: none;

        .MuiOutlinedInput-notchedOutline {
            border-color: ${theme.colors.gray300};
            border-width: 1px;
            display: none;

            legend {
                display: none;
            }
        }

        &:hover .MuiOutlinedInput-notchedOutline {
            border-color: ${theme.colors.gray400};
        }

        &.Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: ${theme.colors.action};
            border-width: 1px;
        }
    }

    .MuiInputAdornment-root {
        color: ${theme.colors.textMuted};
        margin-right: 2px;
    }

    .MuiInputBase-input {
        padding: 12px 0;
        font-size: 14px;
    }
`;

export const ServicesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;

    @media (max-width: 1024px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

export const ServiceCardButton = styled.button`
    border: 1px solid ${theme.colors.gray200};
    border-radius: 12px;
    background: ${theme.colors.paper};
    color: ${theme.colors.text};
    padding: 16px;
    min-height: 88px;
    text-align: left;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 10px;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
    box-shadow: ${theme.colors.shadowSm};

    &:hover {
        border-color: ${theme.colors.action};
        box-shadow: ${theme.colors.shadowMd};
        transform: translateY(-1px);
    }
`;

export const ServiceCardTitle = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.35;
`;

export const CartShortcutButton = styled(ServiceCardButton)`
    border-color: ${theme.colors.warning};
    background: ${theme.colors.warningBg};
    color: ${theme.colors.text};
`;

export const EmptyState = styled.p`
    margin: 0;
    text-align: center;
    color: ${theme.colors.textMuted};
    font-size: 14px;
    padding: 18px 10px;
`;

export const GroupStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

export const EstimateLayoutContainer = styled.section`
    width: 100%;
    background: ${theme.colors.paper};
    border: 1px solid ${theme.colors.gray300};
    border-radius: 14px;
    box-shadow: ${theme.colors.shadowSm};
    padding: 18px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

export const MaintenanceGroupWrapper = styled.section`
    border: 1px solid ${theme.colors.gray300};
    border-radius: 14px;
    overflow: hidden;
    background: ${({ $softBackground }) => $softBackground || theme.colors.infoBg};
    box-shadow: ${theme.colors.shadowMd};
    display: flex;
    flex-direction: column;
    gap: 0;
`;

export const GroupHeader = styled.div`
    padding: 12px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background: ${({ $background }) => $background || theme.colors.info};
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

export const GroupTitle = styled.h3`
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: ${theme.colors.paper};
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

export const GroupMeta = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: rgba(255, 255, 255, 0.92);
    font-size: 13px;
    font-weight: 600;
`;

export const GroupPill = styled.span`
    border-radius: 999px;
    padding: 2px 10px;
    font-size: 12px;
    font-weight: 700;
    border: 1px solid rgba(255, 255, 255, 0.46);
    background: rgba(255, 255, 255, 0.16);
    color: ${theme.colors.paper};
`;

export const MaintenanceBody = styled.div`
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const AddressBlock = styled.div`
    border: 1px solid ${theme.colors.gray200};
    background: ${theme.colors.gray50};
    border-radius: 10px;
    padding: 10px 12px;
    color: ${theme.colors.textSecondary};
    font-size: 13px;
`;

export const AddressSectionWrapper = styled.div`
    border: 0;
    border-radius: 0;
    background: transparent;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const AddressSectionTitle = styled.h4`
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: ${theme.colors.textSecondary};
`;

export const WarningBox = styled.div`
    border: 1px solid ${theme.colors.warning};
    background: ${theme.colors.warningBg};
    border-radius: 12px;
    padding: 12px;
    color: ${theme.colors.text};
    display: flex;
    flex-direction: column;
    gap: 6px;

    strong {
        font-size: 14px;
        color: ${theme.colors.warning};
    }

    p {
        margin: 0;
        font-size: 13px;
        color: ${theme.colors.textSecondary};
    }
`;

export const QuoteItemsList = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
`;

export const QuoteItemCard = styled.div`
    border: 1px solid ${theme.colors.gray200};
    border-radius: 12px;
    padding: 14px;
    background: ${theme.colors.paper};
    box-shadow: ${theme.colors.shadowSm};
`;

export const QuoteItemHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
`;

export const QuoteItemTitle = styled.h4`
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: ${theme.colors.text};
`;

export const QuoteItemDetails = styled.ul`
    margin: 10px 0 0;
    padding-left: 18px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    color: ${theme.colors.textSecondary};
    font-size: 13px;
`;

export const QuoteItemActions = styled.div`
    display: inline-flex;
    gap: 8px;
    flex-wrap: wrap;
`;

export const CartSummary = styled.div`
    border-top: 1px solid ${theme.colors.gray200};
    padding-top: 12px;
    color: ${theme.colors.textSecondary};
    font-size: 13px;
`;

export const CartActions = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
`;

export const ModalBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

export const ModalFormGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 12px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

export const FieldCol = styled.div`
    grid-column: span ${({ $span }) => $span || 12};

    @media (max-width: 768px) {
        grid-column: span 1;
    }
`;

export const FieldLabel = styled.label`
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
    color: ${({ $error }) => ($error ? theme.colors.danger : theme.colors.text)};
`;

export const FieldErrorText = styled.p`
    margin: 4px 0 0;
    font-size: 12px;
    color: ${theme.colors.danger};
`;

export const ModalFooterButtons = styled.div`
    display: flex;
    gap: 10px;
    width: 100%;

    button {
        flex: 1;
    }

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

export const RadioRow = styled.div`
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
`;

export const ChipList = styled.div`
    display: inline-flex;
    gap: 6px;
    flex-wrap: wrap;
`;

export const ObservationsHint = styled.p`
    margin: 4px 0 0;
    font-size: 12px;
    color: ${theme.colors.textMuted};
`;
